import { LearningItemDto, LearningPathDto, ParentCourseDto } from './dtos';

export type LearningProgressStatus = 'ready' | 'started' | 'locked' | 'completed';
export type ProgressLesson = { item: LearningItemDto; status: LearningProgressStatus; order: number };
export type ProgressProduct = {
    key: string; title: string; standalone: boolean; lessons: ProgressLesson[];
    completed: number; percentage: number; status: LearningProgressStatus | 'empty';
};

/** Module completion is global, while ordering and completion totals belong to each product. */
export function learningProgress(path: LearningPathDto): ProgressProduct[] {
    const lessons = new Map<string, { item: LearningItemDto; status: LearningProgressStatus; parents: Map<string, ParentCourseDto> }>();
    // Later categories win: a completed lesson stays completed everywhere; locks match the access check.
    const categories: [LearningProgressStatus, LearningItemDto[]][] = [
        ['ready', path.available], ['started', path.inProgress], ['locked', path.locked], ['completed', path.completed]
    ];
    for (const [status, items] of categories) for (const item of items) {
        const parents = lessons.get(item.key)?.parents ?? new Map<string, ParentCourseDto>();
        for (const parent of item.parentCourses ?? []) parents.set(parent.key, parent);
        lessons.set(item.key, { item, status, parents });
    }
    const products = new Map<string, { title: string; lessons: ProgressLesson[] }>();
    const assigned = path.assignedCourses ? new Set(path.assignedCourses.map(course => course.key)) : null;
    for (const course of path.assignedCourses ?? []) products.set(course.key, { title: course.title, lessons: [] });
    const standalone: ProgressLesson[] = [];
    for (const lesson of lessons.values()) {
        const parents = [...lesson.parents.values()].filter(parent => !assigned || assigned.has(parent.key));
        if (!parents.length) standalone.push({ item: lesson.item, status: lesson.status, order: lesson.item.orderIndex });
        for (const parent of parents) {
            const product = products.get(parent.key) ?? { title: parent.title, lessons: [] };
            product.lessons.push({ item: lesson.item, status: lesson.status, order: parent.orderIndex ?? lesson.item.orderIndex });
            products.set(parent.key, product);
        }
    }
    const summarize = (key: string, title: string, items: ProgressLesson[], standalone: boolean): ProgressProduct => {
        const completed = items.filter(lesson => lesson.status === 'completed').length;
        const status = !items.length ? 'empty' : completed === items.length ? 'completed'
            : completed || items.some(lesson => lesson.status === 'started') ? 'started'
            : items.some(lesson => lesson.status === 'ready') ? 'ready' : 'locked';
        return { key, title, standalone, completed, status,
            percentage: !items.length ? 0 : completed === items.length ? 100 : Math.min(99, Math.round(completed / items.length * 100)),
            lessons: [...items].sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity)
                || a.item.title.localeCompare(b.item.title) || a.item.key.localeCompare(b.item.key)) };
    };
    const result = [...products].map(([key, product]) => summarize(key, product.title, product.lessons, false))
        .sort((a, b) => a.title.localeCompare(b.title) || a.key.localeCompare(b.key));
    if (standalone.length) result.push(summarize('standalone', 'Standalone lessons', standalone, true));
    return result;
}
